/**
 * OpenalexRetrievalModal
 *
 * Direct scientific-literature retrieval from OpenAlex. Mirrors OpsRetrievalModal:
 * holds criteria-form state and delegates preview/launch to useOpenalexRetrievalJob.
 * The CPC two-loop is transposed to an OpenAlex Topic two-loop (subfields -> topics).
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type OpenAlexQuery } from '../services/openalexRetrievalService';
import {
  subProjectService,
  type OpenAlexSubfieldSuggestion,
  type OpenAlexTopicSuggestion,
} from '../services/subProjectService';
import OpenalexCriteriaPanel from './OpenalexCriteriaPanel';
import OpenalexPreviewPanel from './OpenalexPreviewPanel';
import { useOpenalexRetrievalJob } from '../hooks/useOpenalexRetrievalJob';

interface OpenalexRetrievalModalProps {
  subProjectId: string;
  onClose: () => void;
}

type Panel = 'criteria' | 'preview';
export type TopicPhase = 'idle' | 'loop1_loading' | 'loop2_loading' | 'complete' | 'error';

const EMPTY_QUERY: OpenAlexQuery = {
  keywords_title_abstract: '',
  keywords_title: '',
  keywords_fulltext: '',
  author: [],
  topics: [],
};

export default function OpenalexRetrievalModal({ subProjectId, onClose }: OpenalexRetrievalModalProps) {
  const { t } = useTranslation();

  const [panel, setPanel] = useState<Panel>('criteria');
  const [query, setQuery] = useState<OpenAlexQuery>(EMPTY_QUERY);

  // Topic suggestion state — two-phase
  const [topicPhase, setTopicPhase] = useState<TopicPhase>('idle');
  const [subfields, setSubfields] = useState<OpenAlexSubfieldSuggestion[]>([]);
  const [topicSuggestions, setTopicSuggestions] = useState<OpenAlexTopicSuggestion[]>([]);
  const [topicError, setTopicError] = useState<string | null>(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(new Set());

  const job = useOpenalexRetrievalJob(subProjectId);

  // Topic suggestions: Loop 1 then auto-trigger Loop 2
  useEffect(() => {
    let cancelled = false;
    setTopicPhase('loop1_loading');
    setTopicError(null);

    subProjectService.suggestTopics(subProjectId)
      .then((sf) => {
        if (cancelled) return;
        setSubfields(sf);
        setTopicPhase('loop2_loading');
        return subProjectService.refineTopics(subProjectId, sf.map((s) => s.id));
      })
      .then((suggestions) => {
        if (cancelled || !suggestions) return;
        setTopicSuggestions(suggestions);
        setTopicPhase('complete');
      })
      .catch((err) => {
        if (cancelled) return;
        setTopicError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load topic suggestions');
        setTopicPhase('error');
      });

    return () => { cancelled = true; };
  }, [subProjectId]);

  // Sync selected topic IDs into query.topics
  useEffect(() => {
    setQuery((q) => ({ ...q, topics: Array.from(selectedTopicIds) }));
  }, [selectedTopicIds]);

  const toggleTopic = useCallback((id: string) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllTopics = useCallback(() => {
    setSelectedTopicIds(new Set(topicSuggestions.map((s) => s.id)));
  }, [topicSuggestions]);

  const deselectAllTopics = useCallback(() => {
    setSelectedTopicIds(new Set());
  }, []);

  const set = useCallback(<K extends keyof OpenAlexQuery>(key: K, value: OpenAlexQuery[K]) => {
    setQuery((q) => ({ ...q, [key]: value }));
  }, []);

  const hasAnyCriteria = !!(
    query.keywords_title_abstract || query.keywords_title || query.keywords_fulltext ||
    (query.author?.length ?? 0) > 0 || (query.topics?.length ?? 0) > 0
  );

  const handlePreview = async () => {
    await job.startPreview(query);
    setPanel('preview');
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              {t('openalexRetrieval.title', 'Scientific Literature Retrieval — Direct Query (OpenAlex)')}
            </h2>
            {panel === 'preview' && (
              <p className="text-xs text-slate-500">
                {t('openalexRetrieval.preview.subtitle', 'Select the destination knowledge base')}
              </p>
            )}
          </div>
          <span
            className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer leading-none"
            onClick={onClose}
          >
            &times;
          </span>
        </div>

        {panel === 'criteria' ? (
          <OpenalexCriteriaPanel
            query={query}
            set={set}
            hasAnyCriteria={hasAnyCriteria}
            previewing={job.previewing}
            previewError={job.previewError}
            onClose={onClose}
            onPreview={handlePreview}
            topicPhase={topicPhase}
            subfields={subfields}
            topicSuggestions={topicSuggestions}
            topicError={topicError}
            selectedTopicIds={selectedTopicIds}
            onToggleTopic={toggleTopic}
            onSelectAllTopics={selectAllTopics}
            onDeselectAllTopics={deselectAllTopics}
          />
        ) : job.previewResult ? (
          <OpenalexPreviewPanel
            previewResult={job.previewResult}
            samplesLoading={job.samplesLoading}
            samplesError={job.samplesError}
            kbs={job.kbs}
            kbsLoading={job.kbsLoading}
            selectedKbId={job.selectedKbId}
            setSelectedKbId={job.setSelectedKbId}
            newKbName={job.newKbName}
            setNewKbName={job.setNewKbName}
            kbReady={job.kbReady}
            launching={job.launching}
            launchError={job.launchError}
            onModify={() => {
              setPanel('criteria');
              job.resetPreview();
            }}
            onLaunch={(limit) => job.handleLaunch(query, limit)}
          />
        ) : null}
      </div>
    </div>
  );
}
